import { type vec2 } from 'gl-matrix';

import { type Vector3 } from '@canvas/vector-types';

import { type AnyElement, type AnyElementData } from '../all-element-types';
import {
  type ElementData,
  type Element,
  elementType,
  type ElementType,
} from '../element';

/**
 * Represents a single line segment composing a path.
 * @summary Use this in combination with other segments to form a path.
 * @description All properties of this segment are relative to the `start` of the path.
 */
export interface LineSegment {
  type: 'line';
  /**
   * The angle (in radians) this line should be drawn from
   * its starting point.
   */
  angle: number;
  /**
   * The length of the line from its starting point.
   */
  length: number;
}

/**
 * Represents an arc.
 * @summary Use this in combination with other segments to form a path.
 * @description Model is based on {@link https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/arc}
 * @description All properties of this segment are relative to the `start` of the path.
 * @example startAngle: 0, endAngle: Math.PI * 0.5 = ╮
 * @example startAngle: Math.PI * 0.5, endAngle: Math.PI = ╯
 * @example startAngle: Math.PI, endAngle: Math.PI * 1.5 = ╰
 * @example startAngle: Math.PI * -0.5, endAngle: 0 = ╭
 */
export type ArcSegment = {
  type: 'arc';
  /**
   * @description The arc's radius. Must be positive.
   */
  radius: number;
  /**
   * @description The angle at which the arc starts in radians, measured from the positive x-axis.
   */
  startAngle: number;
  /**
   * @description The angle at which the arc ends in radians, measured from the positive x-axis.
   */
  endAngle: number;
  /**
   * @description An optional boolean value. If true, draws the arc counter-clockwise between the start and end angles. The default is false (clockwise).
   */
  counterClockwise?: boolean;
};

export type Vec2RelativeToStart = vec2;

/**
 * Defines a cubic bezier curve.
 * The start point is set externally to this segment definition.
 * In this curves local coordinate system, the start point is at 0,0
 */
export type CubicBezierSegment = {
  /**
   * @description the type of this segment
   */
  type: 'cubic-bezier';
  /**
   * @description the control point for the start of the curve
   * relative to the start point
   */
  control1: Vec2RelativeToStart;
  /**
   * @description the control point for the end of the curve
   * relative to the start point
   */
  control2: Vec2RelativeToStart;
  /**
   * @description the end of the curve
   * relative to the start point
   */
  end: Vec2RelativeToStart;
};

export type Segment = LineSegment | ArcSegment | CubicBezierSegment;
export type SegmentType = Segment['type'];

/**
 * A number between 0 and 1 relative to a distance.
 * @description Used to position anchors relative to a width (left) or height (top).
 */
export type Proportion = number;

/**
 * Where a path is anchored to an element.
 */
export type ElementAnchor = {
  /**
   * A value between 0 and 1 representing the percentange distance from the left of the element's
   * bounding box where the anchor is placed.
   * @description The bounding box is the rectangular bounding box.
   */
  left: Proportion;
  /**
   * A value between 0 and 1 representing the percentange distance from the top of the element's
   * bounding box where the anchor is placed.
   * @description The bounding box is the rectangular bounding box.
   */
  top: Proportion;
};

/**
 * The mode of a path. Affects the following behaviours in the Canvas app:
 * - hit testing
 * - rendering
 */
export enum PathPresentation {
  STRAIGHT = 1,
  DYNAMIC = 2,
}

export enum PathCap {
  NONE = 1,
  ARROW = 2,
}

export enum PathStroke {
  SMALL = 1,
  MEDIUM = 2,
  LARGE = 3,
}

export enum PathStrokeStyle {
  SOLID = 1,
  DASHED = 2,
  DOTTED = 3,
}

export interface PathElementData extends ElementData<ElementType> {
  /**
   * The list of segments which compose this path.
   */
  segments: Segment[];
  /**
   * Where this path starts.
   * @description The start position will be derived based on a connected element if {@link sourceElement} is set.
   * @description The start position will be kept in sync with a {@link sourceElement} if it exists.
   */
  start: vec2;
  /**
   * Where this path ends.
   * @description The end position will be derived based on a connected element if {@link targetElement} is set.
   * @description The end position will be kept in sync with a {@link targetElement} if it exists.
   */
  end: vec2;
  /**
   * The presentation mode of a path.
   * @description Straight paths are rendered with straight lines.
   * @description Dynamic paths are rendered with a curve-finding algorithm in Canvas.
   */
  presentation: PathPresentation;
  /**
   * Indicates the cap rendered at the start of the path
   */
  startCap?: PathCap;
  /**
   * Indicates the cap rendered at the end of the path
   */
  endCap?: PathCap;
  /**
   * The colour of this path.
   */
  color: Vector3;
  /**
   * The stroke width of the path.
   */
  stroke: PathStroke;
  /**
   * The stroke style of the path.
   */
  strokeStyle?: PathStrokeStyle;
}

export interface PathElement extends PathElementData, Element<ElementType> {}

export function isPath(element: AnyElement | undefined): element is PathElement {
  return element?.type === pathElementType;
}

export function isPathData(
  element: AnyElementData | undefined
): element is PathElementData {
  return element?.type === pathElementType;
}

export const strokeToThickness: Record<PathStroke, number> = {
  [PathStroke.SMALL]: 2,
  [PathStroke.MEDIUM]: 4,
  [PathStroke.LARGE]: 6,
};
export const pathElementType = elementType('path');
